import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Panel, Pill } from "@/components/kit";
import { api, errMsg } from "@/lib/api";
import { SectionHead, Toggle } from "@/pages/manage/common";

export default function EmailSettings() {
  const [f, setF] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/platform/smtp").then((r) => setF({ ...r.data, password: "" }));
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/platform/smtp", { ...f, port: Number(f.port) || 465 });
      setF({ ...data, password: "", clear_password: false });
      toast.success("Email settings saved");
    } catch (err) { toast.error(errMsg(err)); }
  };
  const test = async () => {
    setBusy(true);
    try {
      await api.post("/platform/smtp/test", { to: testTo });
      toast.success(`Test email sent to ${testTo}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (!f) return null;
  return (
    <div className="space-y-6">
      <SectionHead title="Email (SMTP)" text="Your own mail server for invites, reminders, fan emails and password resets." />
      <div className="grid gap-6 xl:grid-cols-[1.3fr_1fr]">
        <Panel>
          <form onSubmit={save} className="space-y-4" data-testid="smtp-form">
            <div className="grid grid-cols-[1fr_120px] gap-4">
              <Field label="SMTP host"><Input value={f.host} onChange={set("host")} placeholder="mail.yourdomain.co.uk" data-testid="smtp-host-input" /></Field>
              <Field label="Port"><Input type="number" value={f.port} onChange={set("port")} data-testid="smtp-port-input" /></Field>
            </div>
            <Field label="Username"><Input value={f.username} onChange={set("username")} autoComplete="off" data-testid="smtp-username-input" /></Field>
            <Field label="Password" hint={f.has_password ? "A password is saved — leave blank to keep it" : "Stored securely; never shown again"}>
              <Input type="password" value={f.password} onChange={set("password")} autoComplete="new-password" data-testid="smtp-password-input" />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="From email"><Input type="email" value={f.from_email} onChange={set("from_email")} placeholder="hello@yourdomain.co.uk" data-testid="smtp-from-email-input" /></Field>
              <Field label="From name"><Input value={f.from_name} onChange={set("from_name")} placeholder="SportsDiary" data-testid="smtp-from-name-input" /></Field>
            </div>
            <Toggle checked={f.enabled} onChange={(v) => setF({ ...f, enabled: v })} label="Use these settings for all platform email" testid="smtp-enabled-toggle" />
            {f.has_password && <Toggle checked={!!f.clear_password} onChange={(v) => setF({ ...f, clear_password: v })} label="Remove the saved password" testid="smtp-clear-password-toggle" />}
            <Button type="submit" className="rounded-full px-8 font-bold uppercase" data-testid="smtp-save-btn">Save</Button>
          </form>
        </Panel>
        <Panel className="h-fit space-y-4">
          <div className="flex items-center gap-2"><p className="font-display text-xl font-bold uppercase text-white">Send a test</p>{f.has_password && f.host ? <Pill tone="green">configured</Pill> : <Pill tone="amber">not set up</Pill>}</div>
          <p className="text-sm text-slate-400">Port 465 uses SSL. Save first, then send a test to confirm delivery.</p>
          <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@email.com" data-testid="smtp-test-to-input" />
          <Button disabled={!testTo || busy} onClick={test} variant="outline" className="w-full rounded-full border-white/15 bg-transparent" data-testid="smtp-test-btn"><Send className="mr-1 h-4 w-4" />{busy ? "Sending…" : "Send test email"}</Button>
        </Panel>
      </div>
    </div>
  );
}
